// pages/setup/Setup.tsx
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, FileCode, Eye, Loader2 } from 'lucide-react';
import {
    getAdSetup,
    createAdSetup,
    updateAdSetup,
    generateScript,
    getRewards,
    getDomains
} from '@/services/api';
import { SetupSidebar, type SetupSidebarTab } from './components/SetupSidebar';
import { PageOverrideEditor, type SiteBlockSnapshot } from './components/PageOverrideEditor';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { PageOverrideBlockKey } from '@/types/ad-setup';
import {
    draftsFromApi,
    draftsToPayload,
    formatApiErrorDetail,
    newUid,
    normalizePagePath,
    validatePageOverrides,
    type PageOverrideBlockDef,
    type PageOverrideDraft,
} from './pageOverrides';
import { BlockEditor } from './components/BlockEditor';
import { LazyLoadEditor } from './components/LazyLoadEditor';
import { ScriptModal } from './components/ScriptModal';
import {SitePreview} from "@/pages/Setup/components/SitePreview.tsx";

interface BlockConfig {
    enabled: boolean;
    include_in: string[];
    customName: string;
    exclude_from: string[];
}

interface DisplayBlockConfig extends BlockConfig {
    lazyLoad: boolean;
    reference: string;
    insideContainer: number;
    positionReference: 'before' | 'after';
}

interface TopoConfig extends DisplayBlockConfig {
    waitReward: number;
}

interface FixedAnchorConfig extends BlockConfig {
    fixedPosition: 'top' | 'bottom';
}

interface RewardConfig extends BlockConfig {
    reward_id: string;
    frequencyPerSession: number;
}

interface InterstitialConfig extends BlockConfig {
    refresh: boolean;
    triggerDelay: number;
    refreshInterval: number;
    triggerOnPageLoad: boolean;
    frequencyPerSession: number;
}

interface LazyLoadConfig {
    enabled: boolean;
    threshold: number;
    color: string;
}

interface AdSetupData {
    domain_id: number;
    Topo: TopoConfig;
    Content1: DisplayBlockConfig;
    Content2: DisplayBlockConfig;
    offerWall: BlockConfig;
    trigger: InterstitialConfig;
    interstitial: InterstitialConfig;
    reward: RewardConfig;
    fixed: FixedAnchorConfig;
    anchor: FixedAnchorConfig;
    lazyLoad: LazyLoadConfig;
}

export default function Setup() {
    const { domainId } = useParams<{ domainId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [setupData, setSetupData] = useState<AdSetupData | null>(null);
    const [selectedBlock, setSelectedBlock] = useState<string>('Topo');
    const [hasChanges, setHasChanges] = useState(false);
    const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
    const [currentDomainName, setCurrentDomainName] = useState<string>('');
    const [isSelectionMode, setIsSelectionMode] = useState(false);

    // Regras por página. Ficam fora do `setupData` de propósito: o resto da tela
    // indexa `setupData` por nome de bloco (`keyof AdSetupData`) e uma lista ali
    // entraria nessa união.
    const [pageOverrides, setPageOverrides] = useState<PageOverrideDraft[]>([]);
    const [sidebarTab, setSidebarTab] = useState<SetupSidebarTab>('blocks');
    const [selectedOverrideUid, setSelectedOverrideUid] = useState<string | null>(null);
    const [overrideToRemove, setOverrideToRemove] = useState<string | null>(null);
    // Bloco da regra selecionada esperando clique no preview.
    const [overridePickBlock, setOverridePickBlock] = useState<PageOverrideBlockKey | null>(null);
    // Erros só aparecem depois da primeira tentativa de salvar, para não
    // pintar de vermelho a regra que a pessoa acabou de criar.
    const [showOverrideErrors, setShowOverrideErrors] = useState(false);

    // Queries
    const { data: existingSetup, isLoading: isLoadingSetup } = useQuery<any>({
        queryKey: ['adSetup', domainId],
        queryFn: () => getAdSetup(parseInt(domainId!)),
        enabled: !!domainId,
    });

    const { data: domains } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: rewards } = useQuery({
        queryKey: ['rewards'],
        queryFn: getRewards,
    });

    // Atualizar o nome do domínio sempre que mudar o domainId ou domains
    useEffect(() => {
        if (domains && domainId) {
            const domain = domains.find((d) => d.id === parseInt(domainId));
            setCurrentDomainName(domain?.domain || 'Carregando...');
        }
    }, [domains, domainId]);

    const currentDomain = domains?.find((d) => d.id === parseInt(domainId!));

    // Mutations
    const createMutation = useMutation({
        mutationFn: createAdSetup,
        onSuccess: () => {
            toast({
                title: 'Setup criado!',
                description: 'Configuração de anúncios criada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['adSetup', domainId] });
            setHasChanges(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar setup',
                description: formatApiErrorDetail(error.response?.data?.detail),
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<AdSetupData> }) =>
            updateAdSetup(id, data),
        onSuccess: () => {
            toast({
                title: 'Setup atualizado!',
                description: 'Configuração de anúncios atualizada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['adSetup', domainId] });
            setHasChanges(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar setup',
                description: formatApiErrorDetail(error.response?.data?.detail),
            });
        },
    });

    const generateScriptMutation = useMutation({
        mutationFn: generateScript,
        onSuccess: (data) => {
            toast({
                title: 'Script gerado!',
                description: `Arquivo criado: ${data.script_path}`,
            });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao gerar script',
                description: formatApiErrorDetail(error.response?.data?.detail),
            });
        },
    });

    // Initialize setup data
    useEffect(() => {
        if (existingSetup) {
            const normalizedData = {
                domain_id: existingSetup.domain_id,
                Topo: {
                    enabled: existingSetup.topo?.enabled ?? true,
                    lazyLoad: existingSetup.topo?.lazy_load ?? false,
                    reference: existingSetup.topo?.reference ?? 'p',
                    include_in: existingSetup.topo?.include_in ?? [],
                    customName: existingSetup.topo?.custom_name ?? '',
                    waitReward: existingSetup.topo?.wait_reward ?? 1,
                    exclude_from: existingSetup.topo?.exclude_from ?? [],
                    insideContainer: existingSetup.topo?.inside_container ?? 1,
                    positionReference: existingSetup.topo?.position_reference ?? 'before',
                },
                Content1: {
                    enabled: existingSetup.content1?.enabled ?? false,
                    lazyLoad: existingSetup.content1?.lazy_load ?? false,
                    reference: existingSetup.content1?.reference ?? 'p',
                    include_in: existingSetup.content1?.include_in ?? [],
                    customName: existingSetup.content1?.custom_name ?? '',
                    exclude_from: existingSetup.content1?.exclude_from ?? [],
                    insideContainer: existingSetup.content1?.inside_container ?? 4,
                    positionReference: existingSetup.content1?.position_reference ?? 'after',
                },
                Content2: {
                    enabled: existingSetup.content2?.enabled ?? false,
                    lazyLoad: existingSetup.content2?.lazy_load ?? false,
                    reference: existingSetup.content2?.reference ?? 'p',
                    include_in: existingSetup.content2?.include_in ?? [],
                    customName: existingSetup.content2?.custom_name ?? '',
                    exclude_from: existingSetup.content2?.exclude_from ?? [],
                    insideContainer: existingSetup.content2?.inside_container ?? 5,
                    positionReference: existingSetup.content2?.position_reference ?? 'after',
                },
                offerWall: {
                    enabled: existingSetup.offerwall?.enabled ?? false,
                    include_in: existingSetup.offerwall?.include_in ?? [],
                    customName: existingSetup.offerwall?.custom_name ?? '',
                    exclude_from: existingSetup.offerwall?.exclude_from ?? [],
                },
                trigger: {
                    enabled: existingSetup.trigger?.enabled ?? false,
                    refresh: existingSetup.trigger?.refresh ?? false,
                    include_in: existingSetup.trigger?.include_in ?? [],
                    customName: existingSetup.trigger?.custom_name ?? '',
                    exclude_from: existingSetup.trigger?.exclude_from ?? [],
                    triggerDelay: existingSetup.trigger?.triggerDelay ?? 500,
                    refreshInterval: existingSetup.trigger?.refreshInterval ?? 30,
                    triggerOnPageLoad: existingSetup.trigger?.triggerOnPageLoad ?? false,
                    frequencyPerSession: existingSetup.trigger?.frequencyPerSession ?? 1,
                },
                interstitial: {
                    enabled: existingSetup.interstitial?.enabled ?? false,
                    refresh: existingSetup.interstitial?.refresh ?? false,
                    include_in: existingSetup.interstitial?.include_in ?? [],
                    customName: existingSetup.interstitial?.custom_name ?? '',
                    exclude_from: existingSetup.interstitial?.exclude_from ?? [],
                    triggerDelay: existingSetup.interstitial?.triggerDelay ?? 1000,
                    refreshInterval: existingSetup.interstitial?.refreshInterval ?? 30,
                    triggerOnPageLoad: existingSetup.interstitial?.triggerOnPageLoad ?? false,
                    frequencyPerSession: existingSetup.interstitial?.frequencyPerSession ?? 1,
                },
                reward: {
                    enabled: existingSetup.reward?.enabled ?? true,
                    include_in: existingSetup.reward?.include_in ?? [],
                    reward_id: existingSetup.reward?.reward_id ?? '1',
                    customName: existingSetup.reward?.custom_name ?? '',
                    exclude_from: existingSetup.reward?.exclude_from ?? [],
                    frequencyPerSession: existingSetup.reward?.frequencyPerSession ?? 1,
                },
                fixed: {
                    enabled: existingSetup.fixed?.enabled ?? false,
                    include_in: existingSetup.fixed?.include_in ?? [],
                    customName: existingSetup.fixed?.custom_name ?? '',
                    exclude_from: existingSetup.fixed?.exclude_from ?? [],
                    fixedPosition: existingSetup.fixed?.fixedPosition ?? 'bottom',
                },
                anchor: {
                    enabled: existingSetup.anchor?.enabled ?? false,
                    include_in: existingSetup.anchor?.include_in ?? [],
                    customName: existingSetup.anchor?.custom_name ?? '',
                    exclude_from: existingSetup.anchor?.exclude_from ?? [],
                    fixedPosition: existingSetup.anchor?.fixedPosition ?? 'top',
                },
                lazyLoad: {
                    color: existingSetup.lazy_load?.color ?? '#3498db',
                    enabled: existingSetup.lazy_load?.enabled ?? true,
                    threshold: existingSetup.lazy_load?.threshold ?? 5000,
                },
            };
            setSetupData(normalizedData);
            // Depois de salvar o GET volta: mantém o id local da regra que não
            // mudou de path, para a seleção não pular.
            setPageOverrides((prev) =>
                draftsFromApi(existingSetup.page_overrides).map((draft, i) =>
                    prev[i] && normalizePagePath(prev[i].path) === draft.path
                        ? { ...draft, uid: prev[i].uid }
                        : draft
                )
            );
        } else if (domainId) {
            setPageOverrides([]);
            // Default setup
            setSetupData({
                domain_id: parseInt(domainId),
                Topo: {
                    enabled: true,
                    lazyLoad: false,
                    reference: 'p',
                    include_in: [],
                    customName: '',
                    waitReward: 1,
                    exclude_from: [],
                    insideContainer: 1,
                    positionReference: 'before',
                },
                Content1: {
                    enabled: false,
                    lazyLoad: false,
                    reference: 'p',
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    insideContainer: 4,
                    positionReference: 'after',
                },
                Content2: {
                    enabled: false,
                    lazyLoad: false,
                    reference: 'p',
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    insideContainer: 5,
                    positionReference: 'after',
                },
                offerWall: {
                    enabled: false,
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                },
                trigger: {
                    enabled: false,
                    refresh: false,
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    triggerDelay: 500,
                    refreshInterval: 30,
                    triggerOnPageLoad: false,
                    frequencyPerSession: 1,
                },
                interstitial: {
                    enabled: false,
                    refresh: false,
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    triggerDelay: 1000,
                    refreshInterval: 30,
                    triggerOnPageLoad: false,
                    frequencyPerSession: 1,
                },
                reward: {
                    enabled: true,
                    include_in: [],
                    reward_id: '1',
                    customName: '',
                    exclude_from: [],
                    frequencyPerSession: 1,
                },
                fixed: {
                    enabled: false,
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    fixedPosition: 'bottom',
                },
                anchor: {
                    enabled: false,
                    include_in: [],
                    customName: '',
                    exclude_from: [],
                    fixedPosition: 'top',
                },
                lazyLoad: {
                    color: '#3498db',
                    enabled: true,
                    threshold: 5000,
                },
            });
        }
    }, [existingSetup, domainId]);

    // Resetar hasChanges quando mudar de domínio
    useEffect(() => {
        setHasChanges(false);
        setSelectedOverrideUid(null);
        setOverridePickBlock(null);
        setShowOverrideErrors(false);
    }, [domainId]);

    const overrideErrors = useMemo(() => validatePageOverrides(pageOverrides), [pageOverrides]);
    const overrideErrorUids = useMemo(
        () => new Set(showOverrideErrors ? overrideErrors.flatMap((e) => (e.uid ? [e.uid] : [])) : []),
        [overrideErrors, showOverrideErrors]
    );

    const handleSave = () => {
        if (!setupData) return;

        if (overrideErrors.length > 0) {
            const first = overrideErrors[0];
            setShowOverrideErrors(true);
            setSidebarTab('pages');
            setSelectedBlock('pageOverrides');
            if (first.uid) setSelectedOverrideUid(first.uid);
            const index = pageOverrides.findIndex((o) => o.uid === first.uid);
            toast({
                variant: 'destructive',
                title: 'Regra por página inválida',
                description:
                    (index >= 0 ? `Regra ${index + 1}: ` : '') +
                    first.message +
                    (overrideErrors.length > 1 ? ` (e mais ${overrideErrors.length - 1})` : ''),
            });
            return;
        }

        const apiData = {
            domain_id: setupData.domain_id,
            topo: {
                enabled: setupData.Topo.enabled,
                lazy_load: setupData.Topo.lazyLoad,
                reference: setupData.Topo.reference,
                include_in: setupData.Topo.include_in,
                custom_name: setupData.Topo.customName,
                wait_reward: setupData.Topo.waitReward,
                exclude_from: setupData.Topo.exclude_from,
                inside_container: setupData.Topo.insideContainer,
                position_reference: setupData.Topo.positionReference,
            },
            content1: {
                enabled: setupData.Content1.enabled,
                lazy_load: setupData.Content1.lazyLoad,
                reference: setupData.Content1.reference,
                include_in: setupData.Content1.include_in,
                custom_name: setupData.Content1.customName,
                exclude_from: setupData.Content1.exclude_from,
                inside_container: setupData.Content1.insideContainer,
                position_reference: setupData.Content1.positionReference,
            },
            content2: {
                enabled: setupData.Content2.enabled,
                lazy_load: setupData.Content2.lazyLoad,
                reference: setupData.Content2.reference,
                include_in: setupData.Content2.include_in,
                custom_name: setupData.Content2.customName,
                exclude_from: setupData.Content2.exclude_from,
                inside_container: setupData.Content2.insideContainer,
                position_reference: setupData.Content2.positionReference,
            },
            offerwall: {
                enabled: setupData.offerWall.enabled,
                include_in: setupData.offerWall.include_in,
                custom_name: setupData.offerWall.customName,
                exclude_from: setupData.offerWall.exclude_from,
            },
            trigger: {
                enabled: setupData.trigger.enabled,
                refresh: setupData.trigger.refresh,
                include_in: setupData.trigger.include_in,
                custom_name: setupData.trigger.customName,
                exclude_from: setupData.trigger.exclude_from,
                triggerDelay: setupData.trigger.triggerDelay,
                refreshInterval: setupData.trigger.refreshInterval,
                triggerOnPageLoad: setupData.trigger.triggerOnPageLoad,
                frequencyPerSession: setupData.trigger.frequencyPerSession,
            },
            interstitial: {
                enabled: setupData.interstitial.enabled,
                refresh: setupData.interstitial.refresh,
                include_in: setupData.interstitial.include_in,
                custom_name: setupData.interstitial.customName,
                exclude_from: setupData.interstitial.exclude_from,
                triggerDelay: setupData.interstitial.triggerDelay,
                refreshInterval: setupData.interstitial.refreshInterval,
                triggerOnPageLoad: setupData.interstitial.triggerOnPageLoad,
                frequencyPerSession: setupData.interstitial.frequencyPerSession,
            },
            reward: {
                enabled: setupData.reward.enabled,
                include_in: setupData.reward.include_in,
                reward_id: setupData.reward.reward_id,
                custom_name: setupData.reward.customName,
                exclude_from: setupData.reward.exclude_from,
                frequencyPerSession: setupData.reward.frequencyPerSession,
            },
            fixed: {
                enabled: setupData.fixed.enabled,
                include_in: setupData.fixed.include_in,
                custom_name: setupData.fixed.customName,
                exclude_from: setupData.fixed.exclude_from,
                fixedPosition: setupData.fixed.fixedPosition,
            },
            anchor: {
                enabled: setupData.anchor.enabled,
                include_in: setupData.anchor.include_in,
                custom_name: setupData.anchor.customName,
                exclude_from: setupData.anchor.exclude_from,
                fixedPosition: setupData.anchor.fixedPosition,
            },
            lazy_load: {
                color: setupData.lazyLoad.color,
                enabled: setupData.lazyLoad.enabled,
                threshold: setupData.lazyLoad.threshold,
            },
            // Lista inteira sempre: `[]` apaga todas as regras na API. Só fica
            // de fora se o GET não trouxe o campo (API antiga) e não há regra —
            // aí mandar `[]` poderia apagar o que a tela nem chegou a ver.
            ...(!existingSetup || Array.isArray(existingSetup.page_overrides) || pageOverrides.length > 0
                ? { page_overrides: draftsToPayload(pageOverrides) }
                : {}),
        };

        if (existingSetup) {
            updateMutation.mutate({
                id: parseInt(domainId!),
                // @ts-ignore
                data: apiData,
            });
        } else {
            createMutation.mutate(apiData);
        }
    };

    const handleGenerateScript = () => {
        if (!domainId) return;
        generateScriptMutation.mutate(parseInt(domainId));
    };

    const updateBlockConfig = <T extends keyof AdSetupData>(
        blockName: T,
        field: string,
        value: any
    ) => {
        if (!setupData) return;

        setSetupData({
            ...setupData,
            [blockName]: {
                // @ts-ignore
                ...setupData[blockName],
                [field]: value,
            },
        });
        setHasChanges(true);
    };

    const updatePageOverride = (next: PageOverrideDraft) => {
        setPageOverrides((list) => list.map((o) => (o.uid === next.uid ? next : o)));
        setHasChanges(true);
    };

    const addPageOverride = () => {
        const draft: PageOverrideDraft = { uid: newUid(), path: '', label: '', blocks: {} };
        setPageOverrides((list) => [...list, draft]);
        setSelectedOverrideUid(draft.uid);
        setSelectedBlock('pageOverrides');
        setOverridePickBlock(null);
        setHasChanges(true);
    };

    const confirmRemovePageOverride = () => {
        if (!overrideToRemove) return;
        setPageOverrides((list) => list.filter((o) => o.uid !== overrideToRemove));
        if (selectedOverrideUid === overrideToRemove) {
            setSelectedOverrideUid(null);
            setOverridePickBlock(null);
        }
        setOverrideToRemove(null);
        setHasChanges(true);
    };

    const handleSelectTab = (tab: SetupSidebarTab) => {
        setSidebarTab(tab);
        setOverridePickBlock(null);
        if (tab === 'pages') setSelectedBlock('pageOverrides');
        else if (selectedBlock === 'pageOverrides') setSelectedBlock(tab === 'config' ? 'lazyLoad' : 'Topo');
    };

    if (isLoadingSetup) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
        );
    }

    if (!setupData) return null;

    // Preparar dados dos blocos para sidebar
    const blockConfigs = {
        Topo: setupData.Topo,
        Content1: setupData.Content1,
        Content2: setupData.Content2,
        anchor: setupData.anchor,
        fixed: setupData.fixed,
        reward: setupData.reward,
        offerWall: setupData.offerWall,
        interstitial: setupData.interstitial,
        trigger: setupData.trigger,
    };

    const getActiveBlocks = () => {
        const result: any = {};

        // Verificar Anchor
        if (setupData.anchor.enabled) {
            result.anchor = {
                enabled: true,
                fixedPosition: setupData.anchor.fixedPosition,
                include_in: setupData.anchor.include_in || [],
                exclude_from: setupData.anchor.exclude_from || []
            };
        }

        if (setupData.fixed.enabled) {
            const fixedPosition = setupData.fixed.fixedPosition;

            if (result.anchor && result.anchor.fixedPosition === fixedPosition) {
            } else {
                result.fixed = {
                    enabled: true,
                    fixedPosition: fixedPosition,
                    include_in: setupData.fixed.include_in || [],
                    exclude_from: setupData.fixed.exclude_from || []
                };
            }
        }
        if (setupData.Topo.enabled) {
            result.Topo = {
                enabled: true,
                reference: setupData.Topo.reference,
                insideContainer: setupData.Topo.insideContainer,
                positionReference: setupData.Topo.positionReference,
                include_in: setupData.Topo.include_in || [],
                exclude_from: setupData.Topo.exclude_from || []
            };
        }

        // Verificar Content1
        if (setupData.Content1.enabled) {
            result.Content1 = {
                enabled: true,
                reference: setupData.Content1.reference,
                insideContainer: setupData.Content1.insideContainer,
                positionReference: setupData.Content1.positionReference,
                include_in: setupData.Content1.include_in || [],
                exclude_from: setupData.Content1.exclude_from || []
            };
        }

        // Verificar Content2
        if (setupData.Content2.enabled) {
            result.Content2 = {
                enabled: true,
                reference: setupData.Content2.reference,
                insideContainer: setupData.Content2.insideContainer,
                positionReference: setupData.Content2.positionReference,
                include_in: setupData.Content2.include_in || [],
                exclude_from: setupData.Content2.exclude_from || []
            };
        }

        return result;
    };



    const activeBlocks = getActiveBlocks();

    const isPagesMode = selectedBlock === 'pageOverrides';
    const selectedOverride = isPagesMode
        ? pageOverrides.find((o) => o.uid === selectedOverrideUid) ?? null
        : null;
    const overrideToRemoveData = pageOverrides.find((o) => o.uid === overrideToRemove);

    // No modo de seleção de uma regra, o preview usa o posicionamento da regra
    // (ou o do site, se a regra herda) para desenhar o marcador.
    const siteKeyOf: Record<PageOverrideBlockKey, PageOverrideBlockDef['siteKey']> = {
        topo: 'Topo',
        content1: 'Content1',
        content2: 'Content2',
        anchor: 'anchor',
        fixed: 'fixed',
        interstitial: 'interstitial',
        trigger: 'trigger',
    };
    const previewBlockConfig =
        isPagesMode && selectedOverride && overridePickBlock
            ? {
                  positionReference: (setupData[siteKeyOf[overridePickBlock]] as SiteBlockSnapshot).positionReference,
                  ...(selectedOverride.blocks[overridePickBlock]?.position_reference
                      ? { positionReference: selectedOverride.blocks[overridePickBlock]?.position_reference }
                      : {}),
              }
            : setupData[selectedBlock as keyof AdSetupData];


    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/domain')}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Setup de Anúncios
                            </h1>
                            <p className="text-sm text-gray-600">
                                {currentDomainName}
                            </p>
                        </div>
                        {hasChanges && (
                            <Badge variant="secondary" className="ml-2">
                                Alterações não salvas
                            </Badge>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => setIsScriptModalOpen(true)}
                            variant="outline"
                            disabled={!existingSetup}
                        >
                            <Eye className="mr-2 h-4 w-4" />
                            Visualizar Script
                        </Button>
                        <Button
                            onClick={handleGenerateScript}
                            variant="outline"
                            disabled={!existingSetup || generateScriptMutation.isPending}
                        >
                            <FileCode className="mr-2 h-4 w-4" />
                            Gerar Script
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={
                                !hasChanges ||
                                createMutation.isPending ||
                                updateMutation.isPending
                            }
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        >
                            <Save className="mr-2 h-4 w-4" />
                            Salvar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 min-h-0">
                {/* Left Panel - Empty for future features */}
                <div className="flex-1 bg-gray-100 border-r border-gray-200 overflow-hidden">
                    <SitePreview
                        selectedBlock={selectedBlock}
                        domain={currentDomain?.domain}
                        blockConfig={previewBlockConfig}
                        activeBlocks={activeBlocks}
                        isSelectionMode={isSelectionMode}
                        onToggleSelectionMode={(enabled) => {
                            setIsSelectionMode(enabled);
                            if (!enabled) setOverridePickBlock(null);
                        }}
                        onSelectElement={(element) => {
                            if (isPagesMode) {
                                // Seleção para a regra: grava no bloco escolhido
                                // em "Selecionar posição no preview".
                                if (!selectedOverride || !overridePickBlock) {
                                    toast({
                                        title: 'Escolha o bloco da regra',
                                        description:
                                            'Na regra, ligue um bloco de Topo/Content e clique em "Selecionar posição no preview".',
                                    });
                                    return;
                                }
                                updatePageOverride({
                                    ...selectedOverride,
                                    blocks: {
                                        ...selectedOverride.blocks,
                                        [overridePickBlock]: {
                                            ...selectedOverride.blocks[overridePickBlock],
                                            reference: element.tag,
                                            inside_container: element.index,
                                        },
                                    },
                                });
                                return;
                            }
                            if (selectedBlock && setupData[selectedBlock as keyof AdSetupData]) {
                                const currentConfig = setupData[selectedBlock as keyof AdSetupData];

                                // Verificar se o bloco tem os campos necessários (apenas blocos de display)
                                // @ts-ignore
                                if ('reference' in currentConfig && 'insideContainer' in currentConfig) {
                                    // Atualizar ambos os campos de uma vez
                                    setSetupData({
                                        ...setupData,
                                        [selectedBlock]: {
                                            ...currentConfig,
                                            reference: element.tag,
                                            insideContainer: element.index,
                                        },
                                    });
                                    setHasChanges(true);
                                }
                            }
                        }}
                    />
                </div>
                {/* Sidebar */}
                <div className="w-50 flex-shrink-0 overflow-y-auto">
                    <SetupSidebar
                        selectedBlock={selectedBlock}
                        onSelectBlock={setSelectedBlock}
                        // @ts-ignore
                        blocks={blockConfigs}
                        selectedTab={sidebarTab}
                        onSelectTab={handleSelectTab}
                        pageOverrides={pageOverrides}
                        selectedOverrideUid={isPagesMode ? selectedOverrideUid : null}
                        overrideErrorUids={overrideErrorUids}
                        onSelectOverride={(uid) => {
                            setSelectedOverrideUid(uid);
                            setSelectedBlock('pageOverrides');
                            setOverridePickBlock(null);
                        }}
                        onAddOverride={addPageOverride}
                        onRemoveOverride={setOverrideToRemove}
                    />
                </div>

                {/* Right Panel - Editor */}
                <div className="w-full lg:w-[350px] flex-shrink-0 overflow-y-auto bg-white border-l border-gray-200 relative">
                    <div className="p-4">
                        {isPagesMode ? (
                            selectedOverride ? (
                                <PageOverrideEditor
                                    draft={selectedOverride}
                                    siteBlocks={setupData}
                                    errors={
                                        showOverrideErrors
                                            ? overrideErrors
                                                  .filter((e) => e.uid === selectedOverride.uid)
                                                  .map((e) => e.message)
                                            : []
                                    }
                                    pickingBlock={overridePickBlock}
                                    onChange={updatePageOverride}
                                    onPickFromPreview={(block) => {
                                        setOverridePickBlock(block);
                                        setIsSelectionMode(block !== null);
                                    }}
                                    onRemove={() => setOverrideToRemove(selectedOverride.uid)}
                                />
                            ) : (
                                <div className="text-center text-xs text-gray-500 py-10 px-4 space-y-2">
                                    <p className="font-medium text-gray-700">Regras por página</p>
                                    <p>
                                        Selecione uma regra na lista ou clique em "Adicionar regra" para
                                        ligar ou desligar blocos numa página específica.
                                    </p>
                                </div>
                            )
                        ) : selectedBlock === 'lazyLoad' ? (
                            <LazyLoadEditor
                                config={setupData.lazyLoad}
                                onUpdate={(field, value) =>
                                    updateBlockConfig('lazyLoad', field, value)
                                }
                            />
                        ) : (
                            <BlockEditor
                                blockName={selectedBlock}
                                config={setupData[selectedBlock as keyof AdSetupData]}
                                rewards={rewards}
                                onUpdate={(field, value) =>
                                    updateBlockConfig(
                                        selectedBlock as keyof AdSetupData,
                                        field,
                                        value
                                    )
                                }
                            />
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={overrideToRemove !== null}
                onOpenChange={(open) => {
                    if (!open) setOverrideToRemove(null);
                }}
                title="Remover regra por página?"
                description={
                    `A regra ${overrideToRemoveData?.path || '(sem path)'} sai da lista. ` +
                    'Ela só é apagada de verdade quando você clicar em Salvar.'
                }
                confirmLabel="Remover"
                variant="danger"
                onConfirm={confirmRemovePageOverride}
            />

            {/* Script Modal */}
            <ScriptModal
                isOpen={isScriptModalOpen}
                onClose={() => setIsScriptModalOpen(false)}
                domainId={parseInt(domainId!)}
            />
        </div>
    );
}